import { useState } from "react";
import { ALL, NONE } from "@miplatform/constants";
import { buildSearchQuery } from "@miplatform/utils";
import {
  OrderSearchFilters,
  OrdersSearchApiRequest,
  OrdersSearchFilterObject,
} from "../OrderSearch.types";
import { ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS } from "../OrdersSearch.constants";

const DEFAULT_SORT = "home/address/addressLine1 asc";
const ORDERS_SEARCH_SORT_MAP: Record<string, string> = {
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.APPROVED_DATE]: "approvedStartDate asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.ASSIGNED_DATE]: "assignedDate asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.BUILDER]: "home/builderName asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.COMMUNITY]: "home/communityName asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.CONFIRMED_SCHEDULE_DATE]:
    "confirmedScheduleDate asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.ORDER_STATUS]: "orderStatus asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.ORDER_TYPE]: "orderTemplateName asc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.RECEIVED_DATE]: "receivedDate desc",
  [ORDERS_SEARCH_GROUP_AND_SORT_BY_KEYS.REQUESTER]: "requester/firstName asc",
};

export const getOrdersSearchSort = (filters: OrderSearchFilters): string[] =>
  [
    filters.groupBy && ORDERS_SEARCH_SORT_MAP[filters.groupBy],
    filters.sortBy && ORDERS_SEARCH_SORT_MAP[filters.sortBy],
    DEFAULT_SORT,
  ].filter(
    (sort, index, sortOptions): sort is string =>
      Boolean(sort) && sortOptions.indexOf(sort) === index
  );

const getArray = (sort?: string | string[]): string[] =>
  (Array.isArray(sort) ? sort : [sort]).filter((option): option is string =>
    Boolean(option?.trim())
  );

export const getOrdersRequestSort = ({
  activeTabSort,
  sidebarSort,
  prioritizeSidebar,
}: OrdersRequestSortParams): string => {
  const activeTabSortOptions = getArray(activeTabSort);
  const sidebarSortOptions = getArray(sidebarSort);
  const sortOptions = prioritizeSidebar
    ? [...sidebarSortOptions, ...activeTabSortOptions]
    : [...activeTabSortOptions, ...sidebarSortOptions];

  return [...new Set(sortOptions)].join(",");
};

const initialOrdersFiltersSearch: OrderSearchFilters = {
  groupBy: NONE,
  sortBy: NONE,
  orderTemplateId: ALL,
  orderStatus: ALL,
  builderId: ALL,
  communityId: ALL,
};

export const useOrdersSearch = ({ search }: UseOrdersSearchProps) => {
  const [filters, setFilters] = useState<OrderSearchFilters>(
    initialOrdersFiltersSearch
  );

  const getOrdersSearchFilterQuery = (params: OrdersSearchApiRequest) => {
    const searchQuery = buildSearchQuery(search, ["home/address/addressLine1"]);
    const filterObj: OrdersSearchFilterObject = { ...searchQuery };

    if (filters) {
      if (filters.builderId && filters.builderId !== ALL) {
        filterObj["home/builderId"] = {
          type: "guid",
          value: filters.builderId,
        };
      }
      if (filters.communityId && filters.communityId !== ALL) {
        filterObj["home/communityId"] = {
          type: "guid",
          value: filters.communityId,
        };
      }

      if (filters.orderTemplateId && filters.orderTemplateId !== ALL) {
        filterObj.orderTemplateId = {
          type: "guid",
          value: filters.orderTemplateId,
        };
      }

      if (filters.orderStatus && filters.orderStatus !== ALL) {
        filterObj.orderStatus = {
          eq: filters.orderStatus,
        };
      }
    }

    return {
      ...params,
      filters: filterObj,
      sort: getOrdersSearchSort(filters),
    };
  };

  const resetFilters = () => {
    setFilters(initialOrdersFiltersSearch);
  };

  return {
    filters,
    setFilters,
    initialFilters: initialOrdersFiltersSearch,
    getOrdersSearchFilterQuery,
    resetFilters,
  };
};

interface UseOrdersSearchProps {
  search: string;
}

interface OrdersRequestSortParams {
  activeTabSort?: string | string[];
  sidebarSort?: string | string[];
  prioritizeSidebar: boolean;
}
